import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex gap-3 py-3" }
const _hoisted_2 = { class: "flex-1 min-w-0" }
const _hoisted_3 = { class: "flex items-center gap-2 mb-1" }
const _hoisted_4 = { class: "font-medium text-sm" }
const _hoisted_5 = { class: "text-xs text-muted-foreground" }
const _hoisted_6 = {
  key: 2,
  class: "text-xs text-muted-foreground"
}
const _hoisted_7 = {
  key: 0,
  class: "space-y-2"
}
const _hoisted_8 = { class: "flex gap-2" }
const _hoisted_9 = {
  key: 1,
  class: "flex items-start gap-2"
}
const _hoisted_10 = { class: "text-sm whitespace-pre-wrap flex-1" }

import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useUsersStore } from '@/views/Users/store'
import type { Comment } from '@/types'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Textarea } from '@/components/ui/textarea'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  Lock,
  Loader2,
} from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'CommentItem',
  props: {
    comment: {}
  },
  emits: ["edit", "delete"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { t } = useI18n()
const authStore = useAuthStore()
const usersStore = useUsersStore()

const isEditing = ref(false)
const editContent = ref('')
const isSubmitting = ref(false)
const showDeleteDialog = ref(false)

const isAdmin = computed(() => authStore.user?.role === 'admin' || authStore.user?.role === 'operator')
const isOwner = computed(() => authStore.user?.id === props.comment.createdBy)
const isGuest = computed(() => !!props.comment.guestEmail)
const canEdit = computed(() => !isGuest.value && (isOwner.value || isAdmin.value))
const canDelete = computed(() => !isGuest.value && (isOwner.value || isAdmin.value))

const authorName = computed(() => {
  if (props.comment.guestEmail) {
    return props.comment.guestName || props.comment.guestEmail.split('@')[0]
  }
  const user = usersStore.users.find((u) => u.id === props.comment.createdBy)
  return user?.displayName || 'Usuario desconocido'
})

const authorInitials = computed(() => {
  const name = authorName.value
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
})

const formattedDate = computed(() => {
  const date = props.comment.createdAt
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Ahora'
  if (minutes < 60) return `Hace ${minutes} min`
  if (hours < 24) return `Hace ${hours}h`
  if (days < 7) return `Hace ${days}d`

  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  })
})

const wasEdited = computed(() => {
  return props.comment.updatedAt.getTime() - props.comment.createdAt.getTime() > 1000
})

const startEdit = () => {
  editContent.value = props.comment.content
  isEditing.value = true
}

const cancelEdit = () => {
  isEditing.value = false
  editContent.value = ''
}

const submitEdit = async () => {
  if (!editContent.value.trim()) return
  isSubmitting.value = true
  try {
    emit('edit', props.comment, editContent.value.trim())
    isEditing.value = false
  } finally {
    isSubmitting.value = false
  }
}

const confirmDelete = () => {
  emit('delete', props.comment)
  showDeleteDialog.value = false
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Avatar), { class: "h-8 w-8" }, {
      default: _withCtx(() => [
        _createVNode(_unref(AvatarFallback), { class: "text-xs" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(authorInitials.value), 1)
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("span", _hoisted_4, _toDisplayString(authorName.value), 1),
        _createElementVNode("span", _hoisted_5, _toDisplayString(formattedDate.value), 1),
        (__props.comment.isInternal)
          ? (_openBlock(), _createBlock(_unref(Badge), {
              key: 0,
              variant: "secondary",
              class: "text-xs"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Lock), { class: "h-3 w-3 mr-1" }),
                _cache[3] || (_cache[3] = _createTextVNode(" Interno ", -1))
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (isGuest.value)
          ? (_openBlock(), _createBlock(_unref(Badge), {
              key: 1,
              variant: "outline",
              class: "text-xs"
            }, {
              default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                _createTextVNode(" Externo ", -1)
              ]))]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (wasEdited.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_6, "(editado)"))
          : _createCommentVNode("", true)
      ]),
      (isEditing.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createVNode(_unref(Textarea), {
              modelValue: editContent.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editContent).value = $event)),
              class: "min-h-[80px]",
              disabled: isSubmitting.value
            }, null, 8, ["modelValue", "disabled"]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_unref(Button), {
                size: "sm",
                onClick: submitEdit,
                disabled: isSubmitting.value || !editContent.value.trim()
              }, {
                default: _withCtx(() => [
                  (isSubmitting.value)
                    ? (_openBlock(), _createBlock(_unref(Loader2), {
                        key: 0,
                        class: "mr-2 h-3 w-3 animate-spin"
                      }))
                    : _createCommentVNode("", true),
                  _cache[5] || (_cache[5] = _createTextVNode(" Guardar ", -1))
                ]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_unref(Button), {
                size: "sm",
                variant: "ghost",
                onClick: cancelEdit,
                disabled: isSubmitting.value
              }, {
                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                  _createTextVNode(" Cancelar ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"])
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createElementVNode("p", _hoisted_10, _toDisplayString(__props.comment.content), 1),
            (canEdit.value || canDelete.value)
              ? (_openBlock(), _createBlock(_unref(DropdownMenu), { key: 0 }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(DropdownMenuTrigger), { "as-child": "" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Button), {
                          variant: "ghost",
                          size: "icon",
                          class: "h-6 w-6 shrink-0"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(MoreHorizontal), { class: "h-4 w-4" })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(DropdownMenuContent), { align: "end" }, {
                      default: _withCtx(() => [
                        (canEdit.value)
                          ? (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                              key: 0,
                              onClick: startEdit
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Pencil), { class: "mr-2 h-4 w-4" }),
                                _createTextVNode(" " + _toDisplayString(_unref(t)('common.edit')), 1)
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true),
                        (canDelete.value)
                          ? (_openBlock(), _createBlock(_unref(DropdownMenuItem), {
                              key: 1,
                              class: "text-destructive",
                              onClick: _cache[1] || (_cache[1] = ($event: any) => (showDeleteDialog.value = true))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Trash2), { class: "mr-2 h-4 w-4" }),
                                _createTextVNode(" " + _toDisplayString(_unref(t)('common.delete')), 1)
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]))
    ]),
    _createVNode(_unref(AlertDialog), {
      open: showDeleteDialog.value,
      "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => ((showDeleteDialog).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(AlertDialogContent), null, {
          default: _withCtx(() => [
            _createVNode(_unref(AlertDialogHeader), null, {
              default: _withCtx(() => [
                _createVNode(_unref(AlertDialogTitle), null, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('comments.deleteComment')), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(AlertDialogDescription), null, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('comments.deleteConfirm')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(AlertDialogFooter), null, {
              default: _withCtx(() => [
                _createVNode(_unref(AlertDialogCancel), null, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('common.cancel')), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(AlertDialogAction), {
                  onClick: confirmDelete,
                  class: "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('common.delete')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"])
  ]))
}
}

})